import { useEffect } from 'react';
import { useTranslation as useI18nTranslation } from 'react-i18next';
import { useLanguage } from '@/contexts/LanguageContext';

/**
 * Custom hook that combines react-i18next's useTranslation with LanguageContext
 *
 * This hook ensures that translations are always in sync with the language
 * selected in LanguageContext. It provides a convenient wrapper around
 * react-i18next's useTranslation hook.
 *
 * @example
 * ```tsx
 * const { t } = useTranslation();
 *
 * <h1>{t('dashboard.totalBalanceDue')}</h1>
 * ```
 */
export function useTranslation() {
  const { currentLanguage } = useLanguage();
  const { t, i18n } = useI18nTranslation();

  // Ensure i18n language is in sync with LanguageContext
  // This is a safety check in case i18n gets out of sync
  useEffect(() => {
    if (i18n.language !== currentLanguage) {
      i18n.changeLanguage(currentLanguage);
    }
  }, [i18n, currentLanguage]);

  return {
    t,
    i18n,
    currentLanguage,
  };
}
